// WB-M1B S3 (doc 23 §19) -- what the board editor does with equations while
// it is running. Pure: no Excalidraw, no DOM, no network. BoardEditor wires
// these to the editor's API; everything that decides something is here, so it
// is decided the same way in the editor and in the specs.
//
// The runtime invariant this module keeps: an image may stay in the scene
// only if it is an equation -- valid Borna metadata, a source the S1
// validator accepts, a valid `em` -- AND its picture is the one THIS client
// drew from that source (the file id is this client's content id for it, and
// the file in the editor is this client's own file). Picture bytes are never
// taken from anywhere else: not from a clipboard, not from another client.
// An image that is not an equation is removed, exactly as in WB-M1A.

import { BOARD_EQUATION_INK, validateEquationSource } from './boardEquation'
import {
  BOARD_EQUATION_DEFAULT_EM,
  BOARD_EQUATION_EM_MAX,
  BOARD_EQUATION_EM_MIN,
  equationBoxAtEm,
  equationEmForHeight,
  isValidEquationEm,
  resizeAtAnchor,
} from './boardEquationGeometry'
import {
  BOARD_ELEMENT_TYPES,
  BOARD_EQUATION_KIND,
  BOARD_EQUATION_VERSION,
  equationPictureFor,
  isRuntimeEquationImage,
  runtimeEquationEm,
} from './boardScene'

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function finite(value, fallback) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

// Excalidraw's own `randomInteger`: seeds and version nonces are 31-bit.
function randomInteger() {
  return Math.floor(Math.random() * 2 ** 31)
}

/**
 * The pictures one editor has drawn, by source: each distinct source is
 * drawn once per editor and its file kept until the editor is destroyed, so
 * undo and redo always find the file an earlier state used.
 * @returns {{get: function(string): object, seed: function(string, object):
 *   void, clear: function(): void}} `get` throws what `equationPictureFor`
 *   throws
 */
export function createEquationPictures() {
  const bySource = new Map()
  return {
    get(source) {
      if (!bySource.has(source)) {
        bySource.set(source, equationPictureFor(source))
      }
      return bySource.get(source)
    },
    seed(source, picture) {
      if (!bySource.has(source)) bySource.set(source, picture)
    },
    clear() {
      bySource.clear()
    },
  }
}

/**
 * What an image element claims as equation metadata, checked: the Borna
 * namespace, `kind`, `v`, a valid `em` and a source the S1 validator
 * accepts. Says nothing about its picture.
 * @param {object} element
 * @returns {{borna: object, latex: string, em: number}|null}
 */
export function equationImageClaim(element) {
  if (!isPlainObject(element) || element.type !== 'image') return null
  const borna =
    isPlainObject(element.customData) && isPlainObject(element.customData.borna)
      ? element.customData.borna
      : null
  if (
    !borna ||
    borna.kind !== BOARD_EQUATION_KIND ||
    borna.v !== BOARD_EQUATION_VERSION ||
    !isValidEquationEm(borna.em) ||
    typeof borna.latex !== 'string'
  ) {
    return null
  }
  try {
    validateEquationSource(borna.latex)
  } catch (err) {
    return null
  }
  return { borna, latex: borna.latex, em: borna.em }
}

/**
 * The `em` a new equation is inserted at: the given one kept inside the
 * range, the default when none (or nothing usable) is given.
 * @param {*} em
 * @returns {number}
 */
export function equationInsertEm(em) {
  if (typeof em !== 'number' || !Number.isFinite(em)) {
    return BOARD_EQUATION_DEFAULT_EM
  }
  return Math.min(BOARD_EQUATION_EM_MAX, Math.max(BOARD_EQUATION_EM_MIN, em))
}

/**
 * A new runtime equation image, ready for the editor's own `restoreElements`
 * (which gives it its element id and the remaining defaults): the picture at
 * natural size x `em`, centred on `center`, unrotated, in board ink (the
 * colour its carrier is written in under frozen WB-M1A).
 * @param {{latex: string, em: number, center: {x: number, y: number},
 *   picture: {fileId: string, metrics: {widthEm: number, heightEm: number}}}}
 *   spec
 */
export function newEquationImage({ latex, em, center, picture }) {
  const width = picture.metrics.widthEm * em
  const height = picture.metrics.heightEm * em
  return {
    type: 'image',
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height,
    angle: 0,
    strokeColor: BOARD_EQUATION_INK,
    backgroundColor: 'transparent',
    fillStyle: 'solid',
    strokeWidth: 2,
    strokeStyle: 'solid',
    roughness: 0,
    opacity: 100,
    roundness: null,
    seed: randomInteger(),
    version: 1,
    versionNonce: randomInteger(),
    isDeleted: false,
    groupIds: [],
    frameId: null,
    boundElements: null,
    link: null,
    locked: false,
    fileId: picture.fileId,
    status: 'saved',
    scale: [1, 1],
    customData: {
      borna: {
        kind: BOARD_EQUATION_KIND,
        v: BOARD_EQUATION_VERSION,
        em,
        latex,
      },
    },
  }
}

/**
 * What changes on a runtime equation image when its source changes: the new
 * picture at the SAME scale, rotated top-left anchor and angle, and a NEW
 * `customData` object (nothing is changed in place) that keeps every other
 * Borna key. To be applied with the editor's `newElementWith`, which gives
 * the element its new version.
 * @param {object} image -- a runtime equation image
 * @param {string} latex -- the new source
 * @param {{fileId: string, metrics: {widthEm: number, heightEm: number}}}
 *   picture -- of the new source
 */
export function equationSourceUpdate(image, latex, picture) {
  const em = runtimeEquationEm(image)
  const width = picture.metrics.widthEm * em
  const height = picture.metrics.heightEm * em
  const { x, y } = resizeAtAnchor(image, width, height)
  return {
    x,
    y,
    width,
    height,
    fileId: picture.fileId,
    status: 'saved',
    customData: Object.assign({}, image.customData, {
      borna: Object.assign({}, image.customData.borna, { em, latex }),
    }),
  }
}

/**
 * What the Equation UI may know about an equation: its id, its source and
 * its scale. No file, no picture, no renderer detail.
 * @param {object} image -- a runtime equation image
 * @returns {{id: string, latex: string, em: number}}
 */
export function equationInfo(image) {
  return {
    id: image.id,
    latex: image.customData.borna.latex,
    em: runtimeEquationEm(image),
  }
}

/**
 * The id of the selected equation: set only when exactly one element is
 * selected and it is a runtime equation image this client drew.
 * @param {ReadonlyArray<object>} elements
 * @param {object} selectedElementIds -- Excalidraw's `{[id]: true}`
 * @returns {string|null}
 */
export function selectedEquationIdOf(elements, selectedElementIds) {
  const selected = selectedElementIds || {}
  let found = null
  let count = 0
  ;(elements || []).forEach(element => {
    if (!element || element.isDeleted || !selected[element.id]) return
    count += 1
    found = element
  })
  return count === 1 && isRuntimeEquationImage(found) ? found.id : null
}

/**
 * The elements of a clipboard, before the editor takes them: every image is
 * either re-made here as this client's own equation, or dropped.
 *   - an image with valid equation metadata keeps its source, its scale and
 *     its anchor; its picture is drawn again from the source, at the picture's
 *     own aspect ratio, and its file id becomes this client's content id.
 *     Whatever file id and bytes the clipboard carried are ignored;
 *   - any other image (a foreign picture, malformed or forged metadata, a
 *     source that is refused or cannot be drawn) is dropped, as in WB-M1A;
 *   - every other element is passed through untouched.
 * The clipboard's files are never used; the caller discards them.
 * @param {ReadonlyArray<object>} elements
 * @param {function(string): object} pictureOf -- `createEquationPictures().get`
 * @returns {{elements: object[], files: object[]}} the elements to paste and
 *   the local files they need
 */
export function sanitizePastedElements(elements, pictureOf) {
  const out = []
  const files = new Map()
  ;(elements || []).forEach(element => {
    if (!isPlainObject(element) || element.type !== 'image') {
      out.push(element)
      return
    }
    const claim = equationImageClaim(element)
    if (!claim) return
    let picture
    try {
      picture = pictureOf(claim.latex)
    } catch (err) {
      return
    }
    const em = equationEmForHeight(
      claim.em,
      element.height,
      picture.metrics.heightEm
    )
    const width = picture.metrics.widthEm * em
    const height = picture.metrics.heightEm * em
    const from = {
      x: finite(element.x, 0),
      y: finite(element.y, 0),
      width: finite(element.width, width),
      height: finite(element.height, height),
      angle: finite(element.angle, 0),
    }
    const { x, y } = resizeAtAnchor(from, width, height)
    files.set(picture.fileId, picture.file)
    out.push(
      Object.assign({}, element, {
        x,
        y,
        width,
        height,
        angle: from.angle,
        fileId: picture.fileId,
        status: 'saved',
        scale: [1, 1],
        customData: Object.assign({}, element.customData, {
          borna: Object.assign({}, claim.borna, { em }),
        }),
      })
    )
  })
  return { elements: out, files: Array.from(files.values()) }
}

/**
 * Enforces the runtime invariant on the editor's scene (see the top of this
 * file). For every live element:
 *   - a WB-M1A element type: kept as it is;
 *   - an equation image this client drew: kept. If the editor's file for it
 *     is missing or is not this client's own file, the file is handed back
 *     (`files`) -- that changes no element, so it never makes the board
 *     dirty. Once a resize has ended (`settled`), its `em` is brought to the
 *     one its height means. A proportional resize inside the `em` range is
 *     left exactly as the editor made it. A geometry that cannot be persisted
 *     exactly -- stretched out of proportion, or beyond either end of the
 *     `em` range -- is normalized there and then (doc 23 §21.2): the
 *     picture's natural size at the clamped `em`, same rotated top-left
 *     anchor, angle, id, source and metadata, so what is on the board is what
 *     a reload reconstructs;
 *   - an image that claims to be an equation but whose file id is not this
 *     client's id for its source: its picture is drawn here and its file id
 *     replaced (when the engine cannot draw it now, it is removed);
 *   - anything else (a foreign image, another element type): removed, as in
 *     WB-M1A.
 * Deleted elements are left alone: they are not persisted, and undo may need
 * them.
 * @param {ReadonlyArray<object>} elements
 * @param {{files: object, pictureOf: function(string): object,
 *   apply: function(object, object): object, settled: boolean}} options --
 *   `files`: the editor's current files; `apply`: the editor's
 *   `newElementWith`; `settled`: no resize is in progress
 * @returns {{elements: ReadonlyArray<object>, changed: boolean,
 *   files: object[]}} `elements` is the given array when nothing changed
 */
export function reconcileBoardElements(elements, options) {
  const { files, pictureOf, apply, settled } = options
  const missing = new Map()
  let changed = false
  const next = []
  ;(elements || []).forEach(element => {
    if (
      !isPlainObject(element) ||
      element.isDeleted ||
      BOARD_ELEMENT_TYPES.indexOf(element.type) !== -1
    ) {
      next.push(element)
      return
    }
    const known = isRuntimeEquationImage(element)
    const claim = known
      ? {
          borna: element.customData.borna,
          latex: element.customData.borna.latex,
          em: element.customData.borna.em,
        }
      : equationImageClaim(element)
    let picture = null
    if (claim) {
      try {
        picture = pictureOf(claim.latex)
      } catch (err) {
        picture = null
      }
    }
    if (!picture) {
      // An equation this client already drew is never removed because its
      // picture cannot be made again right now: it stays as it is.
      if (known) next.push(element)
      else changed = true
      return
    }
    let out = element
    if (out.fileId !== picture.fileId) {
      out = apply(out, { fileId: picture.fileId, status: 'saved' })
    }
    const present = isPlainObject(files) ? files[picture.fileId] : null
    if (!present || present.dataURL !== picture.file.dataURL) {
      missing.set(picture.fileId, picture.file)
    }
    if (settled) {
      const em = equationEmForHeight(
        claim.em,
        out.height,
        picture.metrics.heightEm
      )
      const box = equationBoxAtEm(out, picture.metrics, em)
      if (em !== claim.em || box) {
        // One new element object: Excalidraw's own update semantics, outside
        // history (the resize itself is the history step).
        const updates = Object.assign({}, box)
        if (em !== claim.em) {
          updates.customData = Object.assign({}, out.customData, {
            borna: Object.assign({}, claim.borna, { em }),
          })
        }
        out = apply(out, updates)
      }
    }
    if (out !== element) changed = true
    next.push(out)
  })
  return {
    elements: changed ? next : elements,
    changed,
    files: Array.from(missing.values()),
  }
}
